import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './MyBookings.css';

const STATUS_COLORS = {
  confirmed: 'badge-green',
  pending: 'badge-accent',
  cancelled: 'badge-red',
  completed: 'badge-gray'
};

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const res = await axios.get('/api/bookings/my');
      setBookings(res.data.bookings);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    setCancellingId(bookingId);
    try {
      await axios.put(`/api/bookings/${bookingId}/cancel`);
      setBookings(prev =>
        prev.map(b => b._id === bookingId ? { ...b, status: 'cancelled' } : b)
      );
    } catch (err) {
      alert('Failed to cancel booking');
    } finally {
      setCancellingId(null);
    }
  };

  const getNights = (checkIn, checkOut) => {
    return Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="my-bookings-page">
      <Navbar />
      <div className="my-bookings-container container">
        <div className="page-header">
          <div>
            <h1>My Trips</h1>
            <p className="page-sub">View and manage your bookings</p>
          </div>
          <Link to="/hotels" className="btn btn-dark">Explore Hotels →</Link>
        </div>

        {loading ? (
          <div className="page-loader"><div className="spinner" /></div>
        ) : bookings.length === 0 ? (
          <div className="no-bookings">
            <div className="no-bookings-icon">🗺</div>
            <h3>No trips yet</h3>
            <p>Your adventures await! Start by exploring our curated collection of hotels.</p>
            <Link to="/hotels" className="btn btn-dark btn-lg">Find a Stay</Link>
          </div>
        ) : (
          <div className="bookings-list">
            {bookings.map(booking => {
              const nights = getNights(booking.checkIn, booking.checkOut);
              return (
                <div key={booking._id} className="booking-item">
                  <div className="booking-img">
                    <img
                      src={booking.hotel?.photos?.[0] || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400'}
                      alt={booking.hotel?.name}
                    />
                    <span className={`status-badge badge ${STATUS_COLORS[booking.status]}`}>
                      {booking.status}
                    </span>
                  </div>
                  <div className="booking-details">
                    <div className="booking-main">
                      <div>
                        <h3 className="booking-hotel-name">{booking.hotel?.name}</h3>
                        <p className="booking-location">📍 {booking.hotel?.city}</p>
                        <div className="booking-room-badge">
                          <span className="badge badge-accent">{booking.room?.title}</span>
                          <span className="booking-code">#{booking.confirmationCode}</span>
                        </div>
                      </div>
                      <div className="booking-price-col">
                        <span className="booking-total">${booking.totalPrice?.toLocaleString()}</span>
                        <span className="booking-total-label">total</span>
                      </div>
                    </div>

                    <div className="booking-dates-row">
                      <div className="booking-date">
                        <label>Check-in</label>
                        <p>{format(new Date(booking.checkIn), 'EEE, MMM d, yyyy')}</p>
                      </div>
                      <div className="nights-indicator">
                        <div className="nights-line" />
                        <span>{nights}n</span>
                        <div className="nights-line" />
                      </div>
                      <div className="booking-date">
                        <label>Check-out</label>
                        <p>{format(new Date(booking.checkOut), 'EEE, MMM d, yyyy')}</p>
                      </div>
                    </div>

                    <div className="booking-actions">
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => navigate(`/hotels/${booking.hotel?._id}`)}
                      >
                        View Hotel
                      </button>
                      {(booking.status === 'confirmed' || booking.status === 'pending') && (
                        <button
                          className="btn btn-sm cancel-btn"
                          onClick={() => handleCancel(booking._id)}
                          disabled={cancellingId === booking._id}
                        >
                          {cancellingId === booking._id ? 'Cancelling...' : 'Cancel Booking'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MyBookings;
